import { Button, Box, Alert, Typography, Divider, Chip, Tooltip } from '@mui/material';
import { Save, Undo } from '@mui/icons-material';
import dayjs from 'dayjs';
import type { EditableDocumentMutations } from './useEditableDocumentMutations';

export interface TextEditorAction {
  tooltip?: string;
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  startIcon?: React.ReactNode;
  /** Action key for associating messages */
  key?: string;
}

export interface ActionMessages {
  success?: string;
  error?: string;
  onClearSuccess?: () => void;
  onClearError?: () => void;
}

type EditableDocumentAction = 'save' | 'confirm' | 'discard' | 'revert';

interface EditableDocumentData {
  editableDocument?: {
    title?: string | null;
    lastEditedAt?: string | null;
    confirmedAt?: string | null;
    version?: number | null;
  } | null;
}

interface TextEditorActionsProps {
  showAlerts?: boolean;
  data?: EditableDocumentData | null;
  mutations: EditableDocumentMutations;
  isEditorReady: boolean;
  approveDisabled?: boolean;
  visibleActions: 'all' | EditableDocumentAction[];
  title?: string | null;
  /** Show last saved timestamp under the title */
  showLastSaved?: boolean;
  /** Show last confirmed timestamp under the title */
  showLastConfirmed?: boolean;
  /** Called when user clicks Discard (before mutation). Used to skip save-on-unmount. */
  onDiscardRequested?: () => void;
}

export const TextEditorActions = ({
  showAlerts = true,
  data,
  mutations,
  title,
  isEditorReady,
  approveDisabled = false,
  visibleActions,
  showLastSaved = false,
  showLastConfirmed = false,
  onDiscardRequested,
}: TextEditorActionsProps) => {
  const hasDocument = !!data?.editableDocument;
  const isConfirmed = data?.editableDocument?.confirmedAt !== null;
  const isVersionOne = (data?.editableDocument?.version ?? null) === 1;

  const lastEditedAt = data?.editableDocument?.lastEditedAt;
  const confirmedAt = data?.editableDocument?.confirmedAt;

  const formatTimestamp = (timestamp: string | null | undefined): string | null => {
    if (!timestamp) return null;
    try {
      return dayjs(timestamp).format('MMM D, YYYY [at] h:mm A');
    } catch {
      return null;
    }
  };

  const lastSavedText = showLastSaved && lastEditedAt ? formatTimestamp(lastEditedAt) : null;
  const lastConfirmedText = showLastConfirmed && confirmedAt ? formatTimestamp(confirmedAt) : null;

  // Check if an action should be displayed based on visibleActions prop
  const shouldShowAction = (action: EditableDocumentAction): boolean => {
    return visibleActions === 'all' || visibleActions.includes(action);
  };

  /**
   * Helper function to build an action button and its associated messages.
   * Returns null if the action shouldn't be shown, otherwise returns action and message config.
   */
  const buildAction = (
    key: EditableDocumentAction,
    label: string,
    mutation: {
      handle: () => void;
      loading: boolean;
      success: boolean;
      error: string | null;
      setSuccess: (value: boolean) => void;
      setError: (value: string | null) => void;
    },
    variant: 'contained' | 'outlined' | 'text',
    disabledCondition: boolean,
    startIcon?: React.ReactNode,
    tooltip?: string,
    color?: 'success',
  ): { action: TextEditorAction; message: ActionMessages } | null => {
    if (!shouldShowAction(key)) return null;

    return {
      action: {
        key,
        label,
        onClick: mutation.handle,
        loading: mutation.loading,
        disabled: mutation.loading || !isEditorReady || !hasDocument || disabledCondition,
        variant,
        startIcon,
        color,
        tooltip,
      },
      message: {
        success: mutation.success ? EDITABLE_DOCUMENT_MESSAGES[key].success : undefined,
        error: mutation.error ? EDITABLE_DOCUMENT_MESSAGES[key].error(mutation.error) : undefined,
        onClearSuccess: () => mutation.setSuccess(false),
        onClearError: () => mutation.setError(null),
      },
    };
  };

  const actionConfigs = [
    buildAction('revert', 'Revert', mutations.revert, 'outlined', isVersionOne, <Undo />, ''),
    buildAction(
      'discard',
      'Discard',
      mutations.discard,
      'text',
      false,
      <></>,
      'This will revert back to last approved version',
    ),
    buildAction('save', 'Save', mutations.save, 'contained', false, <Save />),
    buildAction(
      'confirm',
      'Approve Changes',
      mutations.confirm,
      'text',
      isConfirmed || approveDisabled,
      <></>,
      approveDisabled ? 'Please wait for images to finish uploading' : undefined,
    ),
  ].filter((item): item is { action: TextEditorAction; message: ActionMessages } => item !== null);

  const displayActions = actionConfigs.map(({ action }): TextEditorAction => {
    if (action.key === 'discard' && onDiscardRequested) {
      return {
        ...action,
        onClick: (): void => {
          onDiscardRequested();
          action.onClick();
        },
      };
    }
    return action;
  });
  const displayMessages = Object.fromEntries(actionConfigs.map(({ action, message }) => [action.key!, message]));

  // Convert messages to alerts for display
  const alerts = Object.entries(displayMessages).flatMap(([key, message]) => {
    const result = [];
    if (message.success) {
      result.push({ key, severity: 'success' as const, message: message.success, onClose: message.onClearSuccess });
    }
    if (message.error) {
      result.push({ key, severity: 'error' as const, message: message.error, onClose: message.onClearError });
    }
    return result;
  });

  // Don't render if there are no actions, no title, and no timestamps
  if (displayActions.length === 0 && !title && !lastSavedText && !lastConfirmedText) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: showAlerts ? 2 : 0,
        height: showAlerts ? '58px' : '46px',
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: showAlerts ? 2 : 0,
          minHeight: '46px',
          padding: '8px 16px',
        }}>
        {(title || lastSavedText || lastConfirmedText) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 0 }}>
            {title && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                <Typography variant="subtitle1" component="div">
                  {title}
                </Typography>
                <Chip label="Editing" color="secondary" size="small" variant="outlined" />
              </Box>
            )}
            {(lastSavedText || lastConfirmedText) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {lastSavedText && (
                  <Typography variant="caption" color="text.secondary">
                    Last saved: {lastSavedText}
                  </Typography>
                )}
                {lastConfirmedText && (
                  <Typography variant="caption" color="text.secondary">
                    Last confirmed: {lastConfirmedText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, ml: title ? 'auto' : 0 }}>
          {displayActions.map((action, index) => (
            <Tooltip title={action.tooltip} key={action.key || index} arrow placement="top">
              <span>
                <Button
                  size="small"
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading}
                  startIcon={action.startIcon}
                  sx={{ borderRadius: '4px' }}>
                  {action.loading ? `${action.label}...` : action.label}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Box>
      </Box>

      <Divider />

      {showAlerts && alerts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {alerts.map((alert) => (
            <Alert key={alert.key} severity={alert.severity} onClose={alert.onClose} sx={{ animation: 'fadeIn 0.3s' }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Message templates for editable document actions
const EDITABLE_DOCUMENT_MESSAGES = {
  save: {
    success: 'Document saved successfully',
    error: (error: string) => `Error saving document: ${error}`,
  },
  discard: {
    success: 'Current version discarded. Reverted to last confirmed version',
    error: (error: string) => `Error discarding version: ${error}`,
  },
  confirm: {
    success: 'Document confirmed',
    error: (error: string) => `Error confirming document: ${error}`,
  },
  revert: {
    success: 'Document reverted to original',
    error: (error: string) => `Error reverting document: ${error}`,
  },
} as const;
